import type { FlowRole } from '../role';
import { teamLabel } from '../role-view';
import type { FlowLayoutSource } from './schema';

export interface ResolveFlowLayoutInput<Id extends string> {
  /** The unsaved, in-progress pick. Applies on every surface (list, account, home). */
  working?: Id | null;
  /** The rep's saved choice for this surface (and table, on the list surface). */
  saved: Id | null | undefined;
  /** The open saved view's pinned layout. List-only in practice: only lists have views. */
  viewPin?: Id | null;
  /** A per-table User Role default (`listByTable`); beats `uiViewDefault`. */
  uiViewOverride?: Id | null;
  /** The User Role's default for this surface; null when no role is in effect. */
  uiViewDefault: Id | null;
  /** The layouts this surface (table) offers; any other candidate is skipped. */
  available: readonly Id[];
  /** The final fallback; callers pass the surface's fallback (`FLOW_LAYOUT_FALLBACK_ID` for account/Home, `FLOW_LAYOUT_TABLE_FALLBACK_ID[table]` for lists). */
  fallback: Id;
}

export interface ResolvedFlowLayout<Id extends string> {
  id: Id;
  source: FlowLayoutSource;
}

/**
 * The layout a surface shows. Order: working → the rep's saved choice → the
 * saved view's pin → the User Role override → the User Role default →
 * `fallback`. A candidate the surface does not offer is skipped. Generic over
 * the id type only: callers pass the facts (role default, fallback), the
 * resolver reads no registry.
 */
export function resolveFlowLayout<Id extends string>(input: ResolveFlowLayoutInput<Id>): ResolvedFlowLayout<Id> {
  const { working, saved, viewPin, uiViewOverride, uiViewDefault, available, fallback } = input;
  const candidates: [Id | null | undefined, FlowLayoutSource][] = [
    [working, 'working'],
    [saved, 'saved'],
    [viewPin, 'view-pin'],
    [uiViewOverride, 'ui-view-default'],
    [uiViewDefault, 'ui-view-default'],
  ];
  for (const [id, source] of candidates) {
    if (id != null && available.includes(id)) return { id, source };
  }
  return { id: fallback, source: 'fallback' };
}

/** The source badge on the menu's checked row. */
export function flowLayoutSourceLabel(source: FlowLayoutSource, uiView: FlowRole | null): string {
  switch (source) {
    case 'working':
      return 'Modified';
    case 'saved':
      return 'Your choice';
    case 'view-pin':
      return 'Saved with this view';
    case 'ui-view-default':
      return uiView === null ? 'Flow default' : `Default for the ${teamLabel(uiView)} role`;
    case 'fallback':
      return 'Flow default';
  }
}
